/**
 * One vendor, in the round.
 *
 * The screen to open before signing a renewal or approving a large payment:
 * what is owed and how overdue, what stands committed for the year ahead, what
 * is parked with them, what tax was withheld, what actually left the bank each
 * month, and whether their bank destination has changed.
 *
 * Read-only by construction — the page that answers "should we keep paying
 * these people" cannot change anything about them.
 */

import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, formatInr } from '../api'
import { Card, ErrorNote, Loading, Note, Pill, Stat } from '../components/ui'

interface Overview {
  party: {
    id: string
    name: string
    kind: string
    status: string
    gstin: string | null
    pan: string | null
    msme_class: string
    payment_terms_days: number
    tds_head: string | null
    is_lender: boolean
  }
  payables: {
    open_bills: number
    outstanding_paise: number
    buckets: Record<string, number>
    oldest_days_overdue: number
    msme_clock: boolean
  }
  in_flight: { count: number; amount_paise: number }
  held: { advance_paise: number; credit_notes_posted_paise: number }
  tax: { tds_this_year_paise: number; taxable_this_year_paise: number }
  commitments: {
    monthly_run_rate_paise: number
    list: {
      id: string
      reference: string
      title: string
      kind: string
      status: string
      frequency: string
      amount_total_paise: number
      monthly_equivalent_paise: number
      starts_on: string
      ends_on: string | null
      term_months: number | null
      auto_renew: boolean
      treatment: string
    }[]
  }
  upcoming: {
    commitment_reference: string
    title: string
    due_on: string
    period_start: string
    period_end: string
    amount_paise: number
    raised: boolean
  }[]
  paid_by_month: { month: string; amount_paise: number }[]
  paid_last_12_months_paise: number
  documents: { bills_recorded: number; bills_value_paise: number; open_orders: number }
  bank_accounts: {
    id: string
    account_number: string
    ifsc: string
    account_name: string
    bank_name: string | null
    status: string
    approved_on: string | null
    added_on: string | null
  }[]
  as_at: string
}

const BUCKET_LABELS: Record<string, string> = {
  not_due: 'Not due',
  '1_30': '1–30 days',
  '31_60': '31–60 days',
  '61_90': '61–90 days',
  over_90: 'Over 90 days',
}

export default function Vendor360() {
  const { id } = useParams<{ id: string }>()
  const overview = useQuery({
    queryKey: ['vendor-overview', id],
    queryFn: () => api.get<Overview>(`/parties/${id}/overview`),
  })

  if (overview.isLoading) return <Loading what="Gathering everything about this vendor" />
  if (overview.error || !overview.data)
    return (
      <Card title="Vendor">
        <ErrorNote error={overview.error} />
      </Card>
    )

  const data = overview.data
  const peak = Math.max(...data.paid_by_month.map((m) => m.amount_paise), 1)

  return (
    <>
      <Card
        title={data.party.name}
        hint={data.party.status.replace(/_/g, ' ')}
        actions={<Link to="/buying/vendors">All vendors</Link>}
      >
        <div className="grid">
          <Stat
            label="Owed now"
            value={formatInr(data.payables.outstanding_paise)}
            note={`${data.payables.open_bills} open bill(s)${
              data.payables.oldest_days_overdue
                ? `, oldest ${data.payables.oldest_days_overdue} days overdue`
                : ''
            }`}
          />
          <Stat
            label="Paid over 12 months"
            value={formatInr(data.paid_last_12_months_paise)}
            note="what actually left the bank"
          />
          <Stat
            label="Committed each month"
            value={formatInr(data.commitments.monthly_run_rate_paise)}
            note={`${data.commitments.list.length} commitment(s)`}
          />
          <Stat
            label="Held with them"
            value={formatInr(data.held.advance_paise)}
            note="advances not yet set against a bill"
          />
        </div>
        {data.payables.msme_clock && (
          <Note kind="warn">
            A registered MSME supplier: section 15 of the MSMED Act caps payment at 45 days
            whatever the agreed terms say, and interest after that is not deductible.
          </Note>
        )}
        <table>
          <tbody>
            <tr>
              <th>GSTIN</th>
              <td className="mono">{data.party.gstin ?? '—'}</td>
              <th>PAN</th>
              <td className="mono">{data.party.pan ?? '—'}</td>
            </tr>
            <tr>
              <th>Payment terms</th>
              <td>{data.party.payment_terms_days} days</td>
              <th>TDS head</th>
              <td>{data.party.tds_head ?? 'none — nothing is withheld'}</td>
            </tr>
            <tr>
              <th>MSME</th>
              <td>{data.party.msme_class.replace(/_/g, ' ')}</td>
              <th>Withheld this year</th>
              <td>
                {formatInr(data.tax.tds_this_year_paise)} on{' '}
                {formatInr(data.tax.taxable_this_year_paise)}
              </td>
            </tr>
            <tr>
              <th>In flight</th>
              <td>
                {data.in_flight.count} payment(s), {formatInr(data.in_flight.amount_paise)}
              </td>
              <th>Credit notes posted</th>
              <td>{formatInr(data.held.credit_notes_posted_paise)}</td>
            </tr>
          </tbody>
        </table>
      </Card>

      <Card title="What is owed, by age" tight>
        <table>
          <thead>
            <tr>
              {Object.keys(BUCKET_LABELS).map((key) => (
                <th key={key} className="num">
                  {BUCKET_LABELS[key]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              {Object.keys(BUCKET_LABELS).map((key) => (
                <td key={key} className="num">
                  {formatInr(data.payables.buckets[key] ?? 0)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </Card>

      <Card title="Commitments" hint={`${data.commitments.list.length}`} tight>
        {data.commitments.list.length === 0 ? (
          <div className="empty">Nothing recurring is committed to this vendor.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Reference</th>
                <th>What</th>
                <th>Every</th>
                <th className="num">Per period</th>
                <th className="num">A month</th>
                <th>Term</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {data.commitments.list.map((c) => (
                <tr key={c.id}>
                  <td className="mono">
                    <Link to="/buying/commitments">{c.reference}</Link>
                  </td>
                  <td>
                    {c.title}
                    <div className="faint">{c.kind.replace(/_/g, ' ')}</div>
                  </td>
                  <td className="muted">{c.frequency.replace(/_/g, ' ')}</td>
                  <td className="num">{formatInr(c.amount_total_paise)}</td>
                  <td className="num">{formatInr(c.monthly_equivalent_paise)}</td>
                  <td className="muted date">
                    {c.starts_on} → {c.ends_on ?? 'open'}
                    {c.auto_renew && <div className="faint">renews by itself</div>}
                  </td>
                  <td>
                    <Pill value={c.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {data.upcoming.length > 0 && (
        <Card title="Falling due next" hint={`${data.upcoming.length}`} tight>
          <table>
            <thead>
              <tr>
                <th>Due</th>
                <th>What</th>
                <th>Period</th>
                <th className="num">Amount</th>
                <th>Raised</th>
              </tr>
            </thead>
            <tbody>
              {data.upcoming.map((u) => (
                <tr key={`${u.commitment_reference}-${u.period_start}`}>
                  <td className="date">{u.due_on}</td>
                  <td>
                    {u.title}
                    <div className="faint mono">{u.commitment_reference}</div>
                  </td>
                  <td className="muted date">
                    {u.period_start} → {u.period_end}
                  </td>
                  <td className="num">{formatInr(u.amount_paise)}</td>
                  <td>{u.raised ? 'yes' : <span className="faint">not yet</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Card title="Paid, month by month" tight>
        <table>
          <thead>
            <tr>
              <th>Month</th>
              <th className="num">Paid</th>
              <th>Share of the biggest month</th>
            </tr>
          </thead>
          <tbody>
            {data.paid_by_month.map((m) => (
              <tr key={m.month}>
                <td className="mono">{m.month}</td>
                <td className="num">{formatInr(m.amount_paise)}</td>
                <td>
                  <div className="share in">
                    <span style={{ width: `${Math.round((m.amount_paise / peak) * 100)}%` }} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card title="Where the money goes" hint={`${data.bank_accounts.length} account(s)`} tight>
        <Note>
          A change of destination is the first thing to look at when money has gone somewhere it
          should not. Numbers are shown masked; the full number is never needed to see that it
          changed.
        </Note>
        <table>
          <thead>
            <tr>
              <th>Account</th>
              <th>IFSC</th>
              <th>Name on the account</th>
              <th>Added</th>
              <th>Approved</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {data.bank_accounts.map((account) => (
              <tr key={account.id}>
                <td className="mono">{account.account_number}</td>
                <td className="mono">{account.ifsc}</td>
                <td>
                  {account.account_name}
                  {account.bank_name && <div className="faint">{account.bank_name}</div>}
                </td>
                <td className="muted date">{account.added_on ?? '—'}</td>
                <td className="muted date">{account.approved_on ?? '—'}</td>
                <td>
                  <Pill value={account.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card title="History" tight>
        <div className="grid">
          <Stat
            label="Bills recorded"
            value={data.documents.bills_recorded}
            note={formatInr(data.documents.bills_value_paise)}
          />
          <Stat label="Open purchase orders" value={data.documents.open_orders} />
          <Stat label="As at" value={data.as_at} note="every figure on this page" />
        </div>
      </Card>
    </>
  )
}
